import type { GridFilterModel, GridFilterItem } from '@mui/x-data-grid-pro';
import type { MetadataFilterValue } from '@/api/generatedApi';
import { isNegated } from './negatedFilterItem';

export const MIN_WILDCARD_FILTER_LENGTH = 2;
const WILDCARD_OPERATORS = new Set(['contains', 'startsWith', 'endsWith']);
const STRING_FIELDS = ['name', 'creator'] as const;
const LABEL_FIELD = 'label';
const BOOLEAN_FIELDS = ['isAssembly', 'isRootAssembly'] as const;
const STATUS_FIELD = 'state';
const EXTENSION_FIELD = 'extension';

type FilterValue = string | number | boolean;

interface IsEmptyFilter {
  operator: 'isEmpty';
  negated?: boolean;
}

interface StringFilter {
  operator: 'eq' | 'contains' | 'startsWith' | 'endsWith' | 'in';
  value: string | string[];
  negated?: boolean;
}

interface DateFilter {
  operator: 'eq' | 'gt' | 'gte' | 'lt' | 'lte';
  value: string;
  negated?: boolean;
}

interface BooleanFilter {
  operator: 'eq';
  value: boolean;
  negated?: boolean;
}

interface MetadataFilter {
  operator: 'eq' | 'contains' | 'startsWith' | 'endsWith' | 'neq' | 'gt' | 'gte' | 'lt' | 'lte' | 'in';
  value: MetadataFilterValue;
  negated?: boolean;
}

type FilterList<T> = (T | IsEmptyFilter)[];

export interface SerializedFilters {
  name?: FilterList<StringFilter>;
  createdAt?: FilterList<DateFilter>;
  creator?: FilterList<StringFilter>;
  state?: FilterList<StringFilter>;
  extension?: FilterList<StringFilter>;
  label?: FilterList<StringFilter>;
  metadata?: Record<string, FilterList<MetadataFilter>>;
  isAssembly?: FilterList<BooleanFilter>;
  isRootAssembly?: FilterList<BooleanFilter>;
  logicOperator?: 'and' | 'or';
}

const STRING_OPERATOR_MAP: Record<string, StringFilter['operator']> = {
  equals: 'eq',
  contains: 'contains',
  startsWith: 'startsWith',
  endsWith: 'endsWith',
  is: 'eq',
};

const DATE_OPERATOR_MAP: Record<string, DateFilter['operator']> = {
  is: 'eq',
  after: 'gt',
  onOrAfter: 'gte',
  before: 'lt',
  onOrBefore: 'lte',
};

const NUMERIC_OPERATOR_MAP: Record<string, MetadataFilter['operator']> = {
  '=': 'eq',
  '!=': 'neq',
  '>': 'gt',
  '>=': 'gte',
  '<': 'lt',
  '<=': 'lte',
};

function isWildcardTooShort(operator: string, value: unknown): boolean {
  return WILDCARD_OPERATORS.has(operator) && typeof value === 'string' && value.length < MIN_WILDCARD_FILTER_LENGTH;
}

export function isWildcardFilterIncomplete(operator: string, value: unknown): boolean {
  return value !== '' && isWildcardTooShort(operator, value);
}

function withNegated<T extends { operator: string }>(filter: T, item: GridFilterItem): T {
  return isNegated(item) ? { ...filter, negated: true } : filter;
}

function serializeFilterItem(
  item: GridFilterItem
): { field: string; filter: StringFilter | DateFilter | MetadataFilter | BooleanFilter | IsEmptyFilter } | undefined {
  if (!item.operator) return undefined;

  if (item.operator === 'isEmpty') {
    return { field: item.field, filter: withNegated({ operator: 'isEmpty' }, item) };
  }

  if ((item.value === undefined || item.value === '') && item.value !== 0) return undefined;
  if (Array.isArray(item.value) && item.value.length === 0) return undefined;

  const { field, operator, value } = item;

  if (STRING_FIELDS.includes(field as (typeof STRING_FIELDS)[number])) {
    const mappedOperator = STRING_OPERATOR_MAP[operator];
    if (!mappedOperator || typeof value !== 'string' || isWildcardTooShort(mappedOperator, value)) return undefined;
    return { field, filter: withNegated({ operator: mappedOperator, value }, item) };
  }

  if (field === 'createdAt') {
    const mappedOperator = DATE_OPERATOR_MAP[operator];
    if (!mappedOperator) return undefined;
    return {
      field,
      filter: withNegated(
        { operator: mappedOperator, value: value instanceof Date ? value.toISOString() : String(value) },
        item
      ),
    };
  }

  if (field === STATUS_FIELD) {
    if (operator === 'isAnyOf' && Array.isArray(value)) {
      return { field, filter: withNegated({ operator: 'in', value }, item) };
    }
    if (operator === 'equals' && typeof value === 'string') {
      return { field, filter: withNegated({ operator: 'eq', value }, item) };
    }
    return undefined;
  }

  if (field === EXTENSION_FIELD) {
    if (typeof value !== 'string') return undefined;
    const normalizedValue = value.startsWith('.') ? value.slice(1) : value;
    if (normalizedValue.length < MIN_WILDCARD_FILTER_LENGTH) return undefined;
    return { field, filter: withNegated({ operator: 'endsWith', value: `.${normalizedValue}` }, item) };
  }

  if (field === LABEL_FIELD) {
    if (operator === 'isAnyOf' && Array.isArray(value)) {
      return { field, filter: withNegated({ operator: 'in', value }, item) };
    }
    const mappedOperator = STRING_OPERATOR_MAP[operator];
    if (!mappedOperator || typeof value !== 'string' || isWildcardTooShort(mappedOperator, value)) return undefined;
    return { field, filter: withNegated({ operator: mappedOperator, value }, item) };
  }

  if (BOOLEAN_FIELDS.includes(field as (typeof BOOLEAN_FIELDS)[number])) {
    if (operator !== 'is') return undefined;
    const boolValue = value === true || value === 'true';
    return { field, filter: withNegated({ operator: 'eq', value: boolValue }, item) };
  }

  if (field.startsWith('metadata.')) {
    const mappedOperator = STRING_OPERATOR_MAP[operator] ?? NUMERIC_OPERATOR_MAP[operator];
    if (!mappedOperator || isWildcardTooShort(mappedOperator, value)) return undefined;
    return { field, filter: withNegated({ operator: mappedOperator, value }, item) };
  }

  return undefined;
}

function getOrFiltersToMerge(filterModel: GridFilterModel): Map<string, FilterValue[]> {
  const result = new Map<string, FilterValue[]>();
  if (filterModel.logicOperator !== 'or') return result;

  const equalityItems = filterModel.items.filter(
    item =>
      (item.operator === 'equals' || item.operator === '=') &&
      item.value !== undefined &&
      item.value !== '' &&
      !isNegated(item)
  );

  const fieldCounts = new Map<string, number>();
  for (const item of equalityItems) {
    fieldCounts.set(item.field, (fieldCounts.get(item.field) ?? 0) + 1);
  }

  for (const [field, count] of fieldCounts) {
    if (count > 1) {
      const values = equalityItems.filter(item => item.field === field).map(item => item.value as FilterValue);
      result.set(field, values);
    }
  }

  return result;
}

function addFilter(
  result: SerializedFilters,
  metadata: Record<string, FilterList<MetadataFilter>>,
  field: string,
  filter: StringFilter | DateFilter | MetadataFilter | BooleanFilter | IsEmptyFilter
): void {
  switch (field) {
    case 'name': {
      result.name ??= [];
      result.name.push(filter as StringFilter);
      break;
    }
    case 'createdAt': {
      result.createdAt ??= [];
      result.createdAt.push(filter as DateFilter);
      break;
    }
    case 'creator': {
      result.creator ??= [];
      result.creator.push(filter as StringFilter);
      break;
    }
    case STATUS_FIELD: {
      result.state ??= [];
      result.state.push(filter as StringFilter);
      break;
    }
    case EXTENSION_FIELD: {
      result.extension ??= [];
      result.extension.push(filter as StringFilter);
      break;
    }
    case 'label': {
      result.label ??= [];
      result.label.push(filter as StringFilter);
      break;
    }
    case 'isAssembly': {
      result.isAssembly ??= [];
      result.isAssembly.push(filter as BooleanFilter);
      break;
    }
    case 'isRootAssembly': {
      result.isRootAssembly ??= [];
      result.isRootAssembly.push(filter as BooleanFilter);
      break;
    }
    default: {
      if (field.startsWith('metadata.')) {
        const metadataFieldName = field.slice('metadata.'.length);
        metadata[metadataFieldName] ??= [];
        metadata[metadataFieldName].push(filter as MetadataFilter);
      }
    }
  }
}

export function serializeFilterModel(filterModel: GridFilterModel | undefined): SerializedFilters | undefined {
  if (!filterModel?.items || filterModel.items.length === 0) return undefined;

  const result: SerializedFilters = {};
  const metadata: Record<string, FilterList<MetadataFilter>> = {};
  const orMerges = getOrFiltersToMerge(filterModel);
  const processedFields = new Set<string>();

  for (const item of filterModel.items) {
    const serialized = serializeFilterItem(item);
    if (!serialized) continue;

    const { field, filter } = serialized;
    if (processedFields.has(field)) continue;

    const mergeValues = orMerges.get(item.field);
    if (mergeValues) {
      processedFields.add(field);
      addFilter(result, metadata, field, { operator: 'in', value: mergeValues } as StringFilter | MetadataFilter);
    } else {
      addFilter(result, metadata, field, filter);
    }
  }

  if (Object.keys(metadata).length > 0) {
    result.metadata = metadata;
  }

  if (filterModel.logicOperator === 'or') {
    result.logicOperator = 'or';
  }

  const hasFilters =
    result.name ||
    result.createdAt ||
    result.creator ||
    result.state ||
    result.extension ||
    result.label ||
    result.metadata ||
    result.isAssembly ||
    result.isRootAssembly;
  return hasFilters ? result : undefined;
}
